import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

/**
 * Invoice status + payment status + holdover pills.
 *
 * Status and payment status are SEPARATE axes on the backend — a part-paid
 * invoice that is later voided must show both facts — so they render as
 * separate pills rather than one merged label.
 */

const STATUS_CLASS = {
    GENERATED: 'v3-inv-pill-generated',
    SENT: 'v3-inv-pill-sent',
    VOID: 'v3-inv-pill-void',
};

const PAYMENT_CLASS = {
    UNPAID: 'v3-inv-pill-unpaid',
    PARTIAL: 'v3-inv-pill-partial',
    PAID: 'v3-inv-pill-paid',
    OVERPAID: 'v3-inv-pill-partial',
};

export function StatusPill({ status }) {
    const { t } = useTranslation();
    if (!status) return null;
    const label = {
        GENERATED: t('Generated'),
        SENT: t('Sent'),
        VOID: t('Void'),
    }[status] || status;
    return <span className={`v3-inv-pill ${STATUS_CLASS[status] || ''}`}>{label}</span>;
}

export function PaymentPill({ paymentStatus }) {
    const { t } = useTranslation();
    // Payments are a parked phase — every invoice is UNPAID today, so showing
    // the pill would be noise. Render only once something has actually moved.
    if (!paymentStatus || paymentStatus === 'UNPAID') return null;
    const label = {
        PARTIAL: t('Partially paid'),
        PAID: t('Paid'),
        OVERPAID: t('Overpaid'),
    }[paymentStatus] || paymentStatus;
    return <span className={`v3-inv-pill ${PAYMENT_CLASS[paymentStatus] || ''}`}>{label}</span>;
}

/**
 * Billed on a lease whose end date had already passed. Prominent by design:
 * with the approval workflow parked, noticing this pill is the review step.
 */
export function HoldoverPill({ isHoldover, expiredOn, compact = false }) {
    const { t } = useTranslation();
    if (!isHoldover) return null;
    return (
        <span
            className="v3-inv-pill v3-inv-pill-holdover"
            title={expiredOn
                ? t('Lease expired on {{date}} but the unit is still trading. Billed on the last known terms.', { date: expiredOn })
                : t('Billed on an expired lease.')}
        >
            <AlertTriangle size={11} />
            {compact ? t('Holdover') : t('Holdover — lease expired')}
        </span>
    );
}

export default StatusPill;
